import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ThemeProvider } from "./ThemeContext";

// Components
import Dashboard from "./pages/Dashboard";
import Transactions from "./pages/Transactions";
import TransactionDetail from "./pages/TransactionDetail";
import Workflows from "./pages/Workflows";
import Contacts from "./pages/Contacts";
import AdminManagement from "./pages/AdminManagement";
import Settings from "./pages/Settings";
import CalendarView from "./pages/CalendarView";
import Reports from "./pages/Reports";
import HelpCenter from "./pages/HelpCenter";
import PlatformAdmin from "./pages/PlatformAdmin";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import AdContent from "./pages/AdContent";
import TasksView from "./pages/TasksView";
import Sidebar from "./components/Sidebar";
import OnboardingTour from "./components/OnboardingTour";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults: always send credentials AND the stored token
axios.interceptors.request.use((config) => {
  config.withCredentials = true;
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [realAdmin, setRealAdmin] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.search?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setImpersonating(response.data.impersonating || false);
      setRealAdmin(response.data.real_admin || null);
    } catch (error) {
      setUser(null);
      localStorage.removeItem("session_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const startImpersonation = async (targetUserId) => {
    try {
      await axios.post(`${API}/admin/impersonate/${targetUserId}`, {});
      await checkAuth();
      toast.success("Impersonation started");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start impersonation");
      return false;
    }
  };

  const stopImpersonation = async () => {
    try {
      await axios.post(`${API}/admin/stop-impersonate`, {});
      setImpersonating(false);
      setRealAdmin(null);
      await checkAuth();
      toast.success("Returned to admin view");
      return true;
    } catch (error) {
      toast.error("Failed to stop impersonation");
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    localStorage.removeItem("session_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, impersonating, realAdmin, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Support session_id in both hash fragment and query params
      const hash = window.location.hash;
      const search = window.location.search;
      let sessionId = null;

      // Try hash first (#session_id=xxx)
      const hashMatch = hash.match(/session_id=([^&]+)/);
      if (hashMatch) sessionId = hashMatch[1];

      // Fallback to query params (?session_id=xxx)
      if (!sessionId) {
        const params = new URLSearchParams(search);
        sessionId = params.get("session_id");
      }

      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const rememberDevice = localStorage.getItem("remember_device") === "true";
        localStorage.removeItem("remember_device");

        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId, remember_device: rememberDevice },
          { timeout: 20000 }
        );
        
        // Store session token in localStorage for mobile compatibility
        // (iOS ITP blocks httpOnly cookies after cross-domain redirects)
        if (response.data.session_token) {
          localStorage.setItem("session_token", response.data.session_token);
        }
        
        setUser(response.data);
        toast.success(`Welcome, ${response.data.name}!`);
        // Clear hash/params and navigate to dashboard
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: response.data } });
      } catch (error) {
        const detail = error.response?.data?.detail || "Authentication failed. Please try again.";
        toast.error(detail);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--t-bg)]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--t-primary)] mx-auto"></div>
        <p className="mt-4 text-[var(--t-text-muted)]">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data was passed from AuthCallback, use it immediately
  if (location.state?.user) {
    return <AppLayout>{children}</AppLayout>;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--t-bg)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--t-primary)] mx-auto"></div>
          <p className="mt-4 text-[var(--t-text-muted)]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

// Impersonation Banner
const ImpersonationBanner = () => {
  const { impersonating, realAdmin, user, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  if (!impersonating) return null;

  const handleStop = async () => {
    const ok = await stopImpersonation();
    if (ok) navigate("/dashboard");
  };

  return (
    <div
      className="flex items-center justify-between px-5 py-2.5"
      style={{ background: "#D97706", color: "#fff" }}
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        Viewing as <strong className="mx-1">{user?.name}</strong> ({user?.email})
        {realAdmin && <span className="opacity-80 ml-1">— logged in as {realAdmin.name}</span>}
      </div>
      <button
        onClick={handleStop}
        className="px-3 py-1 text-xs font-semibold rounded bg-white/20 hover:bg-white/30 transition-colors"
        data-testid="stop-impersonation-btn"
      >
        Stop Impersonating
      </button>
    </div>
  );
};

// App Layout with Sidebar
const AppLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "true"; } catch { return false; }
  });
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleCollapse = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem("sidebar_collapsed", String(next)); } catch {}
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[var(--t-bg)] flex">
      <Sidebar mobileOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} collapsed={sidebarCollapsed} onToggleCollapse={toggleCollapse} />
      <div className="flex-1 flex flex-col overflow-auto min-w-0">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-40 flex items-center gap-3 px-4 py-3 border-b border-[var(--t-border)] bg-[var(--t-bg)]">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-lg hover:bg-[var(--t-card)] text-[var(--t-text)]"
            data-testid="mobile-menu-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <img
            src="https://customer-assets.emergentagent.com/job_contract-flow-21/artifacts/cuiv7qxm_eo-logo-blue-stacked-header-logo.png"
            alt="EasyOffice"
            className="h-7 w-auto"
          />
        </div>
        {/* Desktop expand button when sidebar is collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={toggleCollapse}
            className="hidden lg:flex fixed top-4 left-4 z-40 items-center justify-center w-9 h-9 rounded-lg shadow-md border text-[var(--t-text-muted)] hover:text-[var(--t-text)] hover:shadow-lg transition-all"
            style={{ background: "var(--t-card)", borderColor: "var(--t-border)" }}
            title="Expand sidebar"
            data-testid="sidebar-expand-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
        )}
        <ImpersonationBanner />
        <main className="flex-1">
          {children}
        </main>
      </div>
      <OnboardingTour />
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment or query params for session_id - SYNCHRONOUS check before routes
  if (location.hash?.includes('session_id=') || location.search?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
      <Route path="/transactions/:id" element={<ProtectedRoute><TransactionDetail /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksView /></ProtectedRoute>} />
      <Route path="/workflows" element={<ProtectedRoute><Workflows /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminManagement /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarView /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
      <Route path="/platform-admin" element={<ProtectedRoute><PlatformAdmin /></ProtectedRoute>} />
      <Route path="/ad-content" element={<ProtectedRoute><AdContent /></ProtectedRoute>} />
      <Route path="/" element={<Landing />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
